/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';

import { PaginatedM2MClientsResponseSchema } from '@/client';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { M2MClientFilters } from '@/hooks/query/usePaginatedM2MClientsQuery';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

// Type for individual M2M client from paginated response
type M2MClient = PaginatedM2MClientsResponseSchema['data'][number];

export type M2MClientsTableProps = {
  m2mClientsData: PaginatedM2MClientsResponseSchema;
  m2mClientFilters: M2MClientFilters;
  updateQueryParams: UpdateParams<M2MClient, Partial<M2MClientFilters>>;
  deleteM2MClient: (clientId: string, clientName: string) => void;
  primaryAction?: React.ReactNode;
};

export function M2MClientsTable({
  m2mClientsData,
  updateQueryParams,
  m2mClientFilters,
  deleteM2MClient,
  primaryAction,
}: M2MClientsTableProps) {
  const { t } = useMimicTranslation('m2mClients');
  const navigate = useNavigate();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const handleViewClient = (client: M2MClient) => {
    navigate(`/tenants/${tenantId}/m2m-clients/${client.id}`);
  };

  const columns = getM2MClientColumns(t, m2mClientFilters, handleViewClient, deleteM2MClient);

  return (
    <PaginatedTable
      data={m2mClientsData}
      columns={columns}
      rowKey={(client) => client.id}
      data-testid="m2m-clients-table"
      onParamsChange={updateQueryParams}
      primaryAction={primaryAction}
    />
  );
}

function getM2MClientColumns(
  t: MimicTranslationFunction<'m2mClients'>,
  m2mClientFilters: M2MClientFilters,
  viewClient: (client: M2MClient) => void,
  deleteM2MClient: (clientId: string, clientName: string) => void,
): ColumnsType<M2MClient> {
  return [
    {
      title: <span data-testid="m2mClients.id">{t('table.clientId')}</span>,
      dataIndex: 'id',
      render: (id: string) => {
        return (
          <Text mono copyable size="sm">
            {id}
          </Text>
        );
      },
      width: '280px',
    },
    {
      title: <span data-testid="m2mClients.name">{t('table.name')}</span>,
      dataIndex: 'name',
      sorter: true,
      filterMode: 'menu',
      render: (name) => {
        return (
          <Flex gap="xxs">
            <MimicHighlighter searchText={m2mClientFilters?.name} text={name} />
          </Flex>
        );
      },
    },
    {
      title: <span data-testid="m2mClients.allowedScopes">{t('table.allowedScopes')}</span>,
      dataIndex: 'allowedScopes',
      render: (allowedScopes: string[]) => {
        if (!allowedScopes || allowedScopes.length === 0) {
          return <Text type="subtle">-</Text>;
        }

        const firstScope = allowedScopes[0];
        const displayText = firstScope.length > 20 ? `${firstScope.substring(0, 20)}...` : firstScope;
        const moreCount = allowedScopes.length > 1 ? ` +${allowedScopes.length - 1} ${t('table.more')}` : '';

        const tooltipContent = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {allowedScopes.map((scope) => (
              <code key={scope} style={{ fontSize: '12px' }}>
                {scope}
              </code>
            ))}
          </div>
        );

        return (
          <Tooltip title={tooltipContent}>
            <span>
              <code
                style={{
                  background: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}
              >
                {displayText}
              </code>
              {moreCount && (
                <Text type="subtle" style={{ marginLeft: '4px', fontSize: '12px' }}>
                  {moreCount}
                </Text>
              )}
            </span>
          </Tooltip>
        );
      },
      width: '200px',
    },
    {
      title: <span data-testid="m2mClients.actions">{t('table.actions')}</span>,
      dataIndex: 'actions',
      render: (_, m2mClient: M2MClient) => (
        <Flex gap="xs">
          <Button dataTestId="view-m2m-client" type="default" onClick={() => viewClient(m2mClient)}>
            {t('table.view')}
          </Button>
          <Button
            dataTestId="delete-m2m-client"
            type="primary"
            onClick={() => deleteM2MClient(m2mClient.id, m2mClient.name)}
          >
            {t('table.delete')}
          </Button>
        </Flex>
      ),
      width: '200px',
    },
  ];
}
